import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTeamStore } from "../state/teamStore";
import { useAuthStore } from "../state/authStore";
import { api } from "../utils/api";
import { formatSeriesDisplayName } from "../utils/formatters";
import ConfirmModal from "../components/ConfirmModal";
import type { AvailableSeries, Team, TeamMember } from "../types";

type SeriesDiscipline =
  | "nascar"
  | "short-track"
  | "road-course"
  | "open-wheel"
  | "dirt-oval"
  | "dirt-road"
  | "oval"
  | "other";

type SeriesFormatBucket = "open" | "fixed" | "other";
type TeamSettingsPanel = "series" | "lineup" | "recruit";

const DISCIPLINE_ORDER: SeriesDiscipline[] = [
  "nascar",
  "short-track",
  "road-course",
  "open-wheel",
  "dirt-oval",
  "dirt-road",
  "oval",
  "other",
];

const DISCIPLINE_META: Record<
  SeriesDiscipline,
  { label: string; description: string }
> = {
  nascar: {
    label: "NASCAR",
    description: "Cup, Xfinity, Trucks, ARCA, NiS, eNASCAR, Gen 4 and other stock-car ladders.",
  },
  "short-track": {
    label: "Short Track",
    description: "Late models, modifieds, legends, tour cars, and other short-track weekly series.",
  },
  "road-course": {
    label: "Road Course",
    description: "Sports car, GT, touring car, endurance, and production-car road racing series.",
  },
  "open-wheel": {
    label: "Open Wheel",
    description: "Formula, INDYCAR, Indy NXT, and other open-wheel ladders.",
  },
  "dirt-oval": {
    label: "Dirt Oval",
    description: "Dirt sprint cars, midgets, and other dirt oval weekly championships.",
  },
  "dirt-road": {
    label: "Dirt Road",
    description: "Rallycross and off-road truck series.",
  },
  oval: {
    label: "Oval",
    description: "Non-NASCAR paved oval series that do not fit the stock-car or short-track buckets.",
  },
  other: {
    label: "Other",
    description: "Anything the catalog returned that does not match the main racing disciplines yet.",
  },
};

const FORMAT_ORDER: SeriesFormatBucket[] = ["open", "fixed", "other"];

const FORMAT_META: Record<SeriesFormatBucket, { label: string }> = {
  open: { label: "Open" },
  fixed: { label: "Fixed" },
  other: { label: "Other Formats" },
};

function normalizeSeriesText(series: Pick<AvailableSeries, "name" | "shortCode">): string {
  return `${series.name} ${series.shortCode ?? ""}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function hasSeriesKeyword(series: AvailableSeries, keywords: string[]): boolean {
  const normalized = normalizeSeriesText(series);
  return keywords.some((keyword) => normalized.includes(keyword));
}

function getSeriesDiscipline(series: AvailableSeries): SeriesDiscipline {
  if (hasSeriesKeyword(series, ["dirt oval", "world of outlaws", "dirt sprint", "dirt midget", "dirt late model"])) {
    return "dirt-oval";
  }

  if (hasSeriesKeyword(series, ["rallycross", "pro 2", "pro 4", "off road", "offroad", "stadium truck"])) {
    return "dirt-road";
  }

  if (hasSeriesKeyword(series, ["nascar", "arca", "enascar", "coca cola", "gen 4", "nis"])) {
    return "nascar";
  }

  if (hasSeriesKeyword(series, [
    "late model",
    "modified",
    "legends",
    "tour mod",
    "tour modified",
    "street stock",
    "super late",
    "silver crown",
    "sprint car",
    "midget",
  ])) {
    return "short-track";
  }

  if (hasSeriesKeyword(series, [
    "mazda",
    "mx 5",
    "production car",
    "sim lab",
    "imsa",
    "ferrari",
    "porsche",
    "bmw",
    "gt ",
    "gt3",
    "gt4",
    "touring car",
    "mustang challenge",
    "clio",
    "radical",
    "prototype",
  ])) {
    return "road-course";
  }

  if (hasSeriesKeyword(series, [
    "indycar",
    "indy nxt",
    "formula",
    "super formula",
    "usf",
    "dallara",
    "lotus",
    "skip barber",
  ])) {
    return "open-wheel";
  }

  if (hasSeriesKeyword(series, ["oval", "stock car"])) {
    return "oval";
  }

  return "other";
}

function getSeriesFormatBucket(series: AvailableSeries): SeriesFormatBucket {
  const normalized = normalizeSeriesText(series);
  if (normalized.includes(" fixed ")) {
    return "fixed";
  }
  if (normalized.endsWith(" fixed")) {
    return "fixed";
  }
  if (normalized.includes(" open ")) {
    return "open";
  }
  if (normalized.endsWith(" open")) {
    return "open";
  }
  return "other";
}

function getSeriesPriority(series: AvailableSeries): number {
  const normalized = normalizeSeriesText(series);

  const rules: Array<[RegExp, number]> = [
    [/class a open|aopen/, 10],
    [/class b open|bopen/, 20],
    [/class c open|copen/, 30],
    [/class a fixed|afixed/, 40],
    [/class b fixed|bfixed/, 50],
    [/class c fixed|cfixed/, 60],
    [/\bnis\b|nascar iracing series/, 70],
    [/coca cola iracing series|coca cola series/, 80],
    [/qualifying series/, 90],
    [/gen 4/, 100],
    [/\barca\b/, 110],
    [/late model stock|lmsc/, 120],
    [/sk modified|skmod/, 130],
    [/tour modified|tourmod/, 140],
    [/legends/, 150],
    [/super late/, 160],
    [/advanced mazda|adv mazda|mx 5/, 170],
    [/production endurance|pec/, 180],
    [/production car challenge|pcc/, 190],
    [/imsa|gtp/, 200],
    [/indycar/, 210],
    [/indy nxt/, 220],
  ];

  for (const [pattern, value] of rules) {
    if (pattern.test(normalized)) {
      return value;
    }
  }

  return 1000;
}

function compareSeriesOptions(a: AvailableSeries, b: AvailableSeries): number {
  const priorityDelta = getSeriesPriority(a) - getSeriesPriority(b);
  if (priorityDelta !== 0) {
    return priorityDelta;
  }
  return a.name.localeCompare(b.name);
}

function normalizeSeriesOption(series: AvailableSeries): AvailableSeries {
  return {
    ...series,
    name: formatSeriesDisplayName(series.name),
  };
}

export default function TeamSettings() {
  const { teamId } = useParams<{ teamId: string }>();
  const { user } = useAuthStore();
  const {
    members,
    fetchMembers,
    createTeam,
    setActiveTeam,
    applyTeamUpdate,
  } = useTeamStore();

  const [team, setTeam] = useState<Team | null>(null);
  const [teamLoading, setTeamLoading] = useState(true);
  const [inviteId, setInviteId] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [newTeamName, setNewTeamName] = useState("");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Team profile edit state
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [availableSeries, setAvailableSeries] = useState<AvailableSeries[]>([]);
  const [enabledSeries, setEnabledSeries] = useState<AvailableSeries[]>([]);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesSaving, setSeriesSaving] = useState(false);
  const [seriesMessage, setSeriesMessage] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<TeamSettingsPanel>("series");
  const toggleInFlightRef = useRef(false);
  const [memberActionKey, setMemberActionKey] = useState<string | null>(null);
  const [memberMessage, setMemberMessage] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);

  // Fetch team data from route param
  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    setTeamLoading(true);
    setActiveTeam(teamId);

    api<Team>(`/api/teams/${teamId}`)
      .then((data) => {
        if (cancelled) return;
        setTeam(data);
        setEditName(data.name);
        setEditDescription(data.description || "");
        setEnabledSeries((data.enabledSeries || []).map(normalizeSeriesOption));
        applyTeamUpdate(data);
        setTeamLoading(false);
      })
      .catch(() => {
        if (!cancelled) setTeamLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  useEffect(() => {
    if (teamId) fetchMembers(teamId);
  }, [teamId, fetchMembers]);

  useEffect(() => {
    let cancelled = false;
    setSeriesLoading(true);
    api<AvailableSeries[]>("/api/schedule/series")
      .then((rows) => {
        if (!cancelled) {
          setAvailableSeries(rows.map(normalizeSeriesOption));
          setSeriesLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setSeriesLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const myRole =
    team?.myRole ||
    members.find((m) => m.userId === user?.id)?.role ||
    null;
  const isOwner = myRole === "owner";
  const isAdmin = myRole === "owner" || myRole === "admin";
  const settingsTabs: Array<{ key: TeamSettingsPanel; label: string; meta: string }> = [
    ...(isAdmin ? [{ key: "series" as const, label: "Series cards", meta: `${enabledSeries.length} active` }] : []),
    { key: "lineup" as const, label: "Driver lineup", meta: `${members.length} drivers` },
    ...(isAdmin ? [{ key: "recruit" as const, label: "Recruit drivers", meta: "Invites + links" }] : []),
  ];
  const groupedSeriesSections = DISCIPLINE_ORDER
    .map((discipline) => {
      const disciplineSeries = availableSeries
        .filter((series) => getSeriesDiscipline(series) === discipline)
        .sort(compareSeriesOptions);

      const formatBuckets = FORMAT_ORDER
        .map((format) => ({
          key: format,
          label: FORMAT_META[format].label,
          series: disciplineSeries.filter((series) => getSeriesFormatBucket(series) === format),
        }))
        .filter((bucket) => bucket.series.length > 0);

      return {
        key: discipline,
        label: DISCIPLINE_META[discipline].label,
        description: DISCIPLINE_META[discipline].description,
        seriesCount: disciplineSeries.length,
        formatBuckets,
      };
    })
    .filter((section) => section.seriesCount > 0);

  // Enabled series that are no longer in the synced catalog (e.g. a series that was
  // renamed/removed). They still show on the team page but have no toggle — surface
  // them so an admin can switch them off. (#278)
  const availableSeriesIds = new Set(availableSeries.map((series) => series.seriesId));
  const orphanSeries = enabledSeries
    .filter((series) => !availableSeriesIds.has(series.seriesId))
    .sort(compareSeriesOptions);

  useEffect(() => {
    const activeExists = settingsTabs.some((tab) => tab.key === activePanel);
    if (!activeExists) {
      setActivePanel(settingsTabs[0]?.key ?? "lineup");
    }
  }, [activePanel, settingsTabs]);

  const handleSaveProfile = async () => {
    if (!teamId || !editName.trim()) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const updated = await api<Team>(`/api/teams/${teamId}`, {
        method: "PUT",
        body: JSON.stringify({ name: editName.trim(), description: editDescription.trim() || null }),
      });
      setTeam(updated);
      setEditName(updated.name);
      setEditDescription(updated.description || "");
      applyTeamUpdate(updated);
      setActiveTeam(updated.id);
      setSaveMessage("Saved");
      setTimeout(() => setSaveMessage(null), 2000);
    } catch (err) {
      console.error("Save failed:", err);
      setSaveMessage("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !teamId) return;
    setLogoUploading(true);
    setSaveMessage(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`/api/teams/${teamId}/logo`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) throw new Error("Upload failed");
      const result = await res.json();
      const nextTeam = result.team || { ...team!, logoUrl: result.logoUrl };
      setTeam(nextTeam);
      applyTeamUpdate(nextTeam);
      setActiveTeam(nextTeam.id);
      setSaveMessage("Logo updated");
      setTimeout(() => setSaveMessage(null), 2000);
    } catch (err) {
      console.error("Logo upload failed:", err);
      setSaveMessage(err instanceof Error ? err.message : "Logo upload failed");
    } finally {
      setLogoUploading(false);
      // Reset input so the same file can be re-selected
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleInvite = async () => {
    if (!inviteId || !teamId) return;
    setMemberMessage(null);
    try {
      await api(`/api/teams/${teamId}/invite?iracing_cust_id=${inviteId}`, { method: "POST" });
      setMemberMessage(`Invite sent to #${inviteId}`);
      setInviteId("");
      fetchMembers(teamId);
      setTimeout(() => setMemberMessage(null), 2500);
    } catch {
      // Keep "Failed" as the first word — the message renderer color-codes on it.
      setMemberMessage("Failed to send invite. Check the customer ID and try again.");
    }
  };

  const handleGenerateLink = async () => {
    if (!teamId) return;
    setMemberMessage(null);
    try {
      const result = await api<{ token: string }>(`/api/teams/${teamId}/invite-link`, { method: "POST" });
      setInviteLink(`${window.location.origin}/join/${result.token}`);
    } catch {
      setMemberMessage("Failed to generate an invite link. Try again.");
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    setCreating(true);
    try {
      await createTeam(newTeamName.trim());
      setNewTeamName("");
    } finally {
      setCreating(false);
    }
  };

  const toggleSeries = async (series: AvailableSeries) => {
    if (!teamId || toggleInFlightRef.current) return;
    toggleInFlightRef.current = true;
    setSeriesSaving(true);

    // Compute the new list from current state
    const exists = enabledSeries.some((item) => item.seriesId === series.seriesId);
    const nextList = exists
      ? enabledSeries.filter((item) => item.seriesId !== series.seriesId)
      : [...enabledSeries, series].sort(compareSeriesOptions);

    // Optimistic update
    setEnabledSeries(nextList);

    try {
      const updated = await api<Team>(`/api/teams/${teamId}`, {
        method: "PUT",
        body: JSON.stringify({ enabledSeries: nextList }),
      });
      // Sync from server response (authoritative)
      const serverSeries = (updated.enabledSeries || []).map(normalizeSeriesOption);
      setEnabledSeries(serverSeries);
      setTeam(updated);
      applyTeamUpdate(updated);
    } catch (err) {
      // Revert optimistic update on failure
      console.error("Series toggle failed:", err);
      setEnabledSeries(enabledSeries);
      setSeriesMessage("Failed to save");
      setTimeout(() => setSeriesMessage(null), 2000);
    } finally {
      toggleInFlightRef.current = false;
      setSeriesSaving(false);
    }
  };

  const renderSeriesToggle = (series: AvailableSeries) => {
    const checked = enabledSeries.some((item) => item.seriesId === series.seriesId);

    return (
      <div
        key={series.seriesId}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
          width: "100%",
          padding: "0.95rem 1.15rem",
          minHeight: "4rem",
          background: checked ? "var(--live-soft)" : "rgba(255,255,255,0.015)",
          border: `1px solid ${checked ? "var(--live-edge)" : "var(--rule)"}`,
          borderRadius: "var(--r-md)",
        }}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.8rem",
          minWidth: 0,
          flex: 1,
        }}>
          {series.logoUrl ? (
            <img
              src={series.logoUrl}
              alt=""
              style={{ width: 32, height: 32, objectFit: "contain", flexShrink: 0 }}
            />
          ) : (
            <div style={{
              width: 32,
              height: 32,
              borderRadius: "var(--r-sm)",
              background: "var(--ink-3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-display)",
              fontSize: "0.8125rem",
              color: "var(--text-muted)",
              flexShrink: 0,
            }}>
              {(series.shortCode || series.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontFamily: "var(--font-body)",
              fontSize: "0.9rem",
              fontWeight: 700,
              color: "var(--text-strong)",
              lineHeight: 1.25,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}>
              {formatSeriesDisplayName(series.name)}
            </div>
            {series.shortCode && (
              <div style={{
                fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                fontSize: "0.8125rem",
                color: "var(--text-muted)",
                marginTop: "0.22rem",
              }}>
                {series.shortCode}
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={`${checked ? "Turn off" : "Turn on"} ${series.name}`}
          onClick={() => toggleSeries(series)}
          disabled={seriesSaving}
          style={{
            width: 54,
            height: 30,
            borderRadius: 999,
            border: `1px solid ${checked ? "var(--live-edge)" : "rgba(255,255,255,0.1)"}`,
            background: checked ? "var(--live-soft)" : "rgba(255,255,255,0.04)",
            position: "relative",
            cursor: seriesSaving ? "wait" : "pointer",
            opacity: seriesSaving ? 0.6 : 1,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 3,
              left: checked ? 27 : 3,
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: checked ? "var(--live)" : "rgba(255,255,255,0.75)",
              boxShadow: checked
                ? "0 0 12px var(--live-glow)"
                : "none",
              transition: "left 0.18s ease, background 0.18s ease",
            }}
          />
        </button>
      </div>
    );
  };

  const handleMemberRoleChange = async (
    targetMember: TeamMember,
    nextRole: "admin" | "member"
  ) => {
    if (!teamId) return;
    const actionKey = `${targetMember.userId}:${nextRole}`;
    setMemberActionKey(actionKey);
    setMemberMessage(null);
    try {
      await api(
        `/api/teams/${teamId}/members/${targetMember.userId}/role?role=${encodeURIComponent(nextRole)}`,
        { method: "PUT" }
      );
      await fetchMembers(teamId);
      setMemberMessage(
        nextRole === "admin"
          ? `${targetMember.displayName || "Driver"} promoted to admin`
          : `${targetMember.displayName || "Driver"} set back to member`
      );
      setTimeout(() => setMemberMessage(null), 2500);
    } catch (err) {
      console.error("Role update failed:", err);
      setMemberMessage("Failed to update driver role");
    } finally {
      setMemberActionKey(null);
    }
  };

  const handleRemoveMember = async (targetMember: TeamMember) => {
    if (!teamId) return;
    const actionKey = `${targetMember.userId}:remove`;
    setMemberActionKey(actionKey);
    setMemberMessage(null);
    try {
      await api(`/api/teams/${teamId}/members/${targetMember.userId}`, { method: "DELETE" });
      await fetchMembers(teamId);
      setMemberMessage(`${targetMember.displayName || "Driver"} removed from team`);
      setTimeout(() => setMemberMessage(null), 2500);
    } catch (err) {
      console.error("Member removal failed:", err);
      setMemberMessage("Failed to remove driver");
    } finally {
      setMemberActionKey(null);
      setRemoveTarget(null);
    }
  };

  const renderMemberActions = (member: TeamMember) => {
    if (member.userId === user?.id || member.role === "owner") {
      return null;
    }

    const actions: Array<{
      key: string;
      label: string;
      tone: "blue" | "muted" | "red";
      onClick: () => void | Promise<void>;
    }> = [];

    if (isOwner && member.role === "member") {
      actions.push({
        key: `${member.userId}:admin`,
        label: "Make Admin",
        tone: "blue",
        onClick: () => handleMemberRoleChange(member, "admin"),
      });
    }

    if (isOwner && member.role === "admin") {
      actions.push({
        key: `${member.userId}:member`,
        label: "Make Member",
        tone: "muted",
        onClick: () => handleMemberRoleChange(member, "member"),
      });
    }

    if (isAdmin) {
      actions.push({
        key: `${member.userId}:remove`,
        label: "Remove",
        tone: "red",
        onClick: () => setRemoveTarget(member),
      });
    }

    if (actions.length === 0) {
      return null;
    }

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
        {actions.map((action) => {
          const busy = memberActionKey === action.key;
          const color =
            action.tone === "red"
              ? "var(--flag-red)"
              : action.tone === "blue"
                ? "var(--live)"
                : "var(--text-secondary)";

          const border =
            action.tone === "red"
              ? "rgba(248, 113, 113, 0.22)"
              : action.tone === "blue"
                ? "var(--live-edge)"
                : "var(--rule)";

          const background =
            action.tone === "red"
              ? "var(--flag-red-soft)"
              : action.tone === "blue"
                ? "var(--live-soft)"
                : "var(--ink-2)";

          return (
            <button
              key={action.key}
              className="btn"
              disabled={busy}
              onClick={action.onClick}
              style={{
                fontSize: "0.8125rem",
                color,
                padding: "0.32rem 0.7rem",
                fontFamily: "var(--font-body)",
                letterSpacing: 0,
                border: `1px solid ${border}`,
                background,
                textTransform: "none",
              }}
            >
              {busy ? "Working…" : action.label}
            </button>
          );
        })}
      </div>
    );
  };

  /* ─── Loading ─── */
  if (teamLoading) {
    return (
      <div className="page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <div className="spinner" style={{ width: 28, height: 28 }} />
      </div>
    );
  }

  /* ─── Create Team Flow (no team in route) ─── */
  if (!team && !teamId) {
    return (
      <div className="page" style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
      }}>
        {/* Dramatic create team card */}
        <div style={{
          textAlign: "center",
          marginBottom: "2rem",
        }}>
          {/* Decorative icon */}
          <div style={{
            width: "64px",
            height: "64px",
            margin: "0 auto 1.5rem",
            borderRadius: "50%",
            background: "var(--live-soft)",
            border: "2px solid var(--live-edge)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}>
            {/* Racing flag cross */}
            <div style={{
              width: "24px",
              height: "2px",
              background: "var(--live)",
              boxShadow: "none",
              position: "absolute",
            }} />
            <div style={{
              width: "2px",
              height: "24px",
              background: "var(--live)",
              boxShadow: "none",
              position: "absolute",
            }} />
          </div>

          <h1 style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.75rem",
            fontWeight: 600,
            letterSpacing: "-0.025em",
            textTransform: "none",
            color: "var(--text-strong)",
            marginBottom: "0.5rem",
          }}>
            Create your team
          </h1>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "0.85rem",
            color: "var(--text-muted)",
            maxWidth: "360px",
          }}>
            Start a new racing team to compare setups and share telemetry data with your crew
          </p>
        </div>

        <div className="card" style={{
          maxWidth: 480,
          width: "100%",
          padding: "1.5rem",
          border: "1px solid var(--live-edge)",
        }}>
          <div style={{
            fontFamily: "var(--font-body)",
            fontSize: "0.85rem",
            fontWeight: 500,
            color: "var(--text-secondary)",
            marginBottom: "0.5rem",
          }}>
            Team name
          </div>

          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input
              className="input"
              placeholder="e.g. My Racing Team"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateTeam()}
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "0.95rem",
                padding: "0.65rem 0.85rem",
                background: "var(--ink-2)",
                border: "1px solid var(--live-edge)",
              }}
            />
            <button
              className="btn btn-primary"
              onClick={handleCreateTeam}
              disabled={creating}
              style={{
                padding: "0.65rem 1.5rem",
                whiteSpace: "nowrap",
              }}
            >
              {creating ? (
                <div className="spinner" style={{ width: 16, height: 16 }} />
              ) : (
                "Create team"
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <p style={{ color: "var(--text-secondary)", fontFamily: "var(--font-body)" }}>Team not found</p>
      </div>
    );
  }

  /* ─── Role badge styling ─── */
  const roleBadge = (role: "owner" | "admin" | "member") => {
    const styles: Record<string, { bg: string; color: string; border: string; label: string }> = {
      owner: {
        bg: "var(--flag-yellow-soft)",
        color: "var(--flag-yellow)",
        border: "rgba(250, 204, 21, 0.28)",
        label: "Owner",
      },
      admin: {
        bg: "var(--live-soft)",
        color: "var(--live-strong)",
        border: "var(--live-edge)",
        label: "Admin",
      },
      member: {
        bg: "rgba(220, 230, 245, 0.04)",
        color: "var(--text-default)",
        border: "var(--rule-strong)",
        label: "Member",
      },
    };
    const s = styles[role];

    return (
      <span style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.22rem 0.65rem",
        borderRadius: "9999px",
        fontFamily: "var(--font-body)",
        fontSize: "0.8125rem",
        fontWeight: 500,
        color: s.color,
        background: s.bg,
        border: `1px solid ${s.border}`,
      }}>
        {s.label}
      </span>
    );
  };

  return (
    <div className="page">
      <Link to={`/teams/${teamId}`} className="back-link">
        &larr; Back to team
      </Link>
      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left">
            <span className="eyebrow">Team settings</span>
          </div>
          {isAdmin && (
            <div className="editorial-meta-right">
              <Link to={`/teams/${team.id}/model-health`} className="pill pill--quiet" style={{ textDecoration: "none" }}>
                Projection accuracy →
              </Link>
            </div>
          )}
        </div>
        <h1 className="editorial-title">{team.name}</h1>
        <p className="editorial-dek">
          {team.description || "Team settings and driver lineup"}
        </p>
      </header>

      {/* === Team Profile (admin/owner only) === */}
      {isAdmin && (
        <div className="card" style={{
          marginBottom: "1.75rem",
          padding: 0,
          overflow: "hidden",
          border: "1px solid var(--live-soft)",
        }}>
          {/* Section header */}
          <div style={{
            padding: "0.85rem 1.25rem",
            borderBottom: "1px solid var(--live-soft)",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            background: "transparent",
          }}>
            <div style={{
              width: "4px",
              height: "18px",
              background: "var(--live)",
              borderRadius: "2px",
              boxShadow: "none",
            }} />
             <h3 style={{
               fontFamily: "var(--font-display)",
               fontSize: "1.05rem",
               fontWeight: 700,
               letterSpacing: "-0.01em",
               color: "var(--text-strong)",
            }}>
              Team profile
            </h3>
          </div>

          <div style={{ padding: "1.25rem" }}>
            {/* Logo upload + form row */}
            <div style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start" }}>
              {/* Logo */}
              <div style={{ flexShrink: 0, textAlign: "center" }}>
                <div
                  onClick={() => logoInputRef.current?.click()}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "var(--r-md)",
                    border: "1px dashed var(--rule-strong)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    overflow: "hidden",
                    position: "relative",
                    transition: "border-color 0.2s ease",
                  }}
                >
                  {logoUploading ? (
                    <div className="spinner" style={{ width: 20, height: 20 }} />
                  ) : team.logoUrl ? (
                    <img
                      src={team.logoUrl}
                      alt={`${team.name} logo`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <span style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 700,
                      fontSize: "1.8rem",
                      color: "var(--live)",
                    }}>
                      {(team.name || "?")[0].toUpperCase()}
                    </span>
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  style={{ display: "none" }}
                  onChange={handleLogoUpload}
                />
                <div style={{
                  fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                  fontSize: "0.8125rem",
                  color: "var(--text-secondary)",
                  marginTop: "0.4rem",
                  cursor: "pointer",
                }}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {team.logoUrl ? "Replace" : "Upload logo"}
                </div>
              </div>

              {/* Name + Description form */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                <div>
                  <div style={{
                    fontFamily: "var(--font-body)",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    color: "var(--text-secondary)",
                    marginBottom: "0.35rem",
                  }}>
                    Team name
                  </div>
                  <input
                    className="input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    style={{
                      width: "100%",
                      fontFamily: "var(--font-body)",
                      fontSize: "0.9rem",
                      padding: "0.55rem 0.75rem",
                      background: "var(--ink-2)",
                      border: "1px solid var(--live-edge)",
                    }}
                  />
                </div>
                <div>
                  <div style={{
                    fontFamily: "var(--font-body)",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    color: "var(--text-secondary)",
                    marginBottom: "0.35rem",
                  }}>
                    Description
                  </div>
                  <textarea
                    className="input"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    rows={2}
                    style={{
                      width: "100%",
                      fontFamily: "var(--font-body)",
                      fontSize: "0.85rem",
                      padding: "0.55rem 0.75rem",
                      background: "var(--ink-2)",
                      border: "1px solid var(--live-edge)",
                      resize: "vertical",
                    }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveProfile}
                    disabled={saving || !editName.trim()}
                    style={{
                      padding: "0.5rem 1.25rem",
                    }}
                  >
                    {saving ? (
                      <div className="spinner" style={{ width: 14, height: 14 }} />
                    ) : (
                      "Save"
                    )}
                  </button>
                  {saveMessage && (
                    <span style={{
                      fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                      fontSize: "0.8125rem",
                      color: saveMessage === "Saved" ? "var(--live)" : "var(--flag-red)",
                    }}>
                      {saveMessage}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="tabs" style={{ marginBottom: "1.25rem" }}>
        {settingsTabs.map((tab) => {
          const active = activePanel === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              className={`tab${active ? " active" : ""}`}
              onClick={() => setActivePanel(tab.key)}
            >
              {tab.label}
              <span
                style={{
                  marginLeft: "0.5rem",
                  fontFamily: "var(--font-body)",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  color: "var(--text-muted)",
                }}
              >
                {tab.meta}
              </span>
            </button>
          );
        })}
      </div>

      {isAdmin && activePanel === "series" && (
        <div className="card" style={{
          marginBottom: "1.75rem",
          padding: 0,
          overflow: "hidden",
          border: "1px solid var(--live-soft)",
        }}>
          <div style={{
            padding: "0.85rem 1.25rem",
            borderBottom: "1px solid var(--live-soft)",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            background: "transparent",
          }}>
            <div style={{
              width: "4px",
              height: "18px",
              background: "var(--live)",
              borderRadius: "2px",
              boxShadow: "none",
            }} />
            <h3 style={{
              fontFamily: "var(--font-display)",
              fontSize: "1.05rem",
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--text-strong)",
            }}>
              Series cards
            </h3>
          </div>

          <div style={{ padding: "1.25rem" }}>
            <p style={{
              fontFamily: "var(--font-body)",
              fontSize: "0.96rem",
              fontWeight: 500,
              color: "var(--text-secondary)",
              marginBottom: "1rem",
              lineHeight: 1.45,
            }}>
              Admins can turn series cards on or off here. Only series that are switched on will appear on the team page.
            </p>

            {seriesLoading ? (
              <div className="spinner" style={{ width: 22, height: 22 }} />
            ) : availableSeries.length === 0 ? (
              <p style={{
                fontFamily: "var(--font-body)",
                fontSize: "0.82rem",
                color: "var(--text-muted)",
              }}>
                No series were returned from the schedule catalog.
              </p>
            ) : (
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}>
                {groupedSeriesSections.map((section) => (
                  <div
                    key={section.key}
                    style={{
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: "var(--r-lg)",
                      background: "rgba(255,255,255,0.015)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "0.9rem 1rem",
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        gap: "1rem",
                        borderBottom: "1px solid var(--rule)",
                        background: "transparent",
                        borderLeft: "3px solid var(--live)",
                      }}
                    >
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.25rem",
                      }}>
                        <div style={{
                          fontFamily: "var(--font-display)",
                          fontSize: "1rem",
                          fontWeight: 700,
                          letterSpacing: "-0.01em",
                          color: "var(--text-strong)",
                        }}>
                          {section.label}
                        </div>
                        <div style={{
                          fontFamily: "var(--font-body)",
                          fontSize: "0.92rem",
                          fontWeight: 500,
                          color: "var(--text-secondary)",
                          maxWidth: "820px",
                          lineHeight: 1.45,
                        }}>
                          {section.description}
                        </div>
                      </div>
                      <span style={{
                        fontFamily: "var(--font-body)",
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: "var(--text-secondary)",
                        whiteSpace: "nowrap",
                      }}>
                        {section.seriesCount} series
                      </span>
                    </div>

                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                      padding: "1rem",
                    }}>
                      {section.formatBuckets.map((bucket) => (
                        <div key={`${section.key}-${bucket.key}`}>
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.75rem",
                            marginBottom: "0.65rem",
                            flexWrap: "wrap",
                          }}>
                            <div style={{
                              fontFamily: "var(--font-body)",
                              fontSize: "0.85rem",
                              fontWeight: 600,
                              color: "var(--text-secondary)",
                            }}>
                              {bucket.label}
                            </div>
                            <span
                              style={{
                                fontFamily: "var(--font-body)",
                                fontSize: "0.8125rem",
                                fontWeight: 600,
                                color: "var(--text-secondary)",
                              }}
                            >
                              {bucket.series.length} available
                            </span>
                          </div>
                          <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                            gap: "0.75rem",
                          }}>
                            {bucket.series.map((series) => renderSeriesToggle(series))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {orphanSeries.length > 0 && (
                  <div
                    style={{
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: "var(--r-lg)",
                      background: "rgba(255,255,255,0.015)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "0.9rem 1rem",
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        gap: "1rem",
                        borderBottom: "1px solid var(--rule)",
                        background: "transparent",
                        borderLeft: "3px solid var(--flag-yellow)",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                        <div style={{
                          fontFamily: "var(--font-display)",
                          fontSize: "1rem",
                          fontWeight: 700,
                          letterSpacing: "-0.01em",
                          color: "var(--text-strong)",
                        }}>
                          No longer in the catalog
                        </div>
                        <div style={{
                          fontFamily: "var(--font-body)",
                          fontSize: "0.92rem",
                          fontWeight: 500,
                          color: "var(--text-secondary)",
                          maxWidth: "820px",
                          lineHeight: 1.45,
                        }}>
                          These series are on your team page but aren't in the current iRacing schedule — usually because the series was renamed or retired. Switch one off to clear it.
                        </div>
                      </div>
                      <span style={{
                        fontFamily: "var(--font-body)",
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: "var(--text-secondary)",
                        whiteSpace: "nowrap",
                      }}>
                        {orphanSeries.length} series
                      </span>
                    </div>

                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: "0.75rem",
                      padding: "1rem",
                    }}>
                      {orphanSeries.map((series) => renderSeriesToggle(series))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginTop: "1rem", flexWrap: "wrap" }}>
              <span style={{
                fontFamily: "var(--font-body)",
                fontSize: "0.82rem",
                fontWeight: 600,
                color: seriesSaving ? "var(--live)" : "var(--text-secondary)",
              }}>
                {seriesSaving
                  ? "Saving changes…"
                  : `${enabledSeries.length} ${enabledSeries.length === 1 ? "card" : "cards"} on`}
              </span>
              {seriesMessage && (
                <span style={{
                  fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                  fontSize: "0.8125rem",
                  color: seriesMessage.includes("Failed") ? "var(--flag-red)" : "var(--live)",
                }}>
                  {seriesMessage}
                </span>
              )}
              {!seriesSaving && !seriesMessage && (
                <span style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "0.84rem",
                  color: "var(--text-secondary)",
                }}>
                  Changes save automatically when you flip a switch.
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* === Driver Lineup Board === */}
      {activePanel === "lineup" && (
      <div className="card" style={{
        marginBottom: "1.75rem",
        padding: 0,
        overflow: "hidden",
        border: "1px solid var(--live-soft)",
      }}>
        {/* Section header */}
        <div style={{
          padding: "0.85rem 1.25rem",
          borderBottom: "1px solid var(--live-soft)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(90deg, var(--live-soft) 0%, transparent 60%)",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
          }}>
            <div style={{
              width: "4px",
              height: "18px",
              background: "var(--live)",
              borderRadius: "2px",
              boxShadow: "none",
            }} />
            <h3 style={{
              fontFamily: "var(--font-display)",
              fontSize: "1.05rem",
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--text-strong)",
            }}>
              Driver lineup
            </h3>
          </div>

          <span style={{
            fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
            fontSize: "0.8125rem",
            color: "var(--text-muted)",
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
          }}>
            <span style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: "var(--flag-green)",
              display: "inline-block",
              boxShadow: "none",
            }} />
            {members.length} drivers
          </span>
        </div>

        <div style={{
          padding: "0.7rem 1.25rem",
          borderBottom: "1px solid var(--live-soft)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.75rem",
          flexWrap: "wrap",
        }}>
          <span style={{
            fontFamily: "var(--font-body)",
            fontSize: "0.8125rem",
            color: "var(--text-secondary)",
          }}>
            {isOwner
              ? "Owners can promote members to admin, demote admins, and remove drivers."
              : isAdmin
                ? "Admins can remove drivers. Owner controls promotions and demotions."
                : "View the current team roster and roles."}
          </span>
          {memberMessage && (
            <span style={{
              fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
              fontSize: "0.8125rem",
              color: memberMessage.includes("Failed") ? "var(--flag-red)" : "var(--live)",
            }}>
              {memberMessage}
            </span>
          )}
        </div>

        <table className="table">
          <thead>
            <tr>
              <th style={{ paddingLeft: "1.25rem", width: "50px" }}>
                <span style={{ color: "var(--text-muted)" }}>#</span>
              </th>
              <th>Driver</th>
              <th>iRacing ID</th>
              <th>Position</th>
              <th>Joined</th>
              {isAdmin && <th style={{ width: "260px" }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {members.map((m, index) => (
              <tr key={m.userId} style={{
                background: m.userId === user?.id ? "var(--live-soft)" : undefined,
              }}>
                {/* Row number */}
                <td style={{
                  paddingLeft: "1.25rem",
                  fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                }}>
                  {String(index + 1).padStart(2, "0")}
                </td>

                {/* Driver name */}
                <td>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                  }}>
                    {/* Driver avatar placeholder */}
                    <div style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      background: m.role === "owner"
                        ? "linear-gradient(135deg, var(--flag-yellow-soft) 0%, transparent 100%)"
                        : "linear-gradient(135deg, var(--live-soft) 0%, transparent 100%)",
                      border: `1px solid ${m.role === "owner" ? "var(--flag-yellow-soft)" : "var(--live-edge)"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "var(--font-display)",
                      fontSize: "0.8125rem",
                      fontWeight: 700,
                      color: m.role === "owner" ? "var(--flag-yellow)" : "var(--live)",
                    }}>
                      {(m.displayName || "?")[0].toUpperCase()}
                    </div>
                    <span style={{
                      fontFamily: "var(--font-body)",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      color: m.userId === user?.id ? "var(--live)" : "var(--text-strong)",
                    }}>
                      {m.displayName || "\u2014"}
                      {m.userId === user?.id && (
                        <span style={{
                          fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                          fontSize: "0.8125rem",
                          color: "var(--text-muted)",
                          marginLeft: "0.5rem",
                          fontWeight: 400,
                        }}>
                          (you)
                        </span>
                      )}
                    </span>
                  </div>
                </td>

                {/* iRacing ID */}
                <td style={{
                  fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                  fontSize: "0.8125rem",
                  color: "var(--text-secondary)",
                  letterSpacing: "0.02em",
                }}>
                  {m.iracingCustId}
                </td>

                {/* Role badge */}
                <td>
                  {roleBadge(m.role)}
                </td>

                {/* Joined date */}
                <td style={{
                  fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                }}>
                  {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : "\u2014"}
                </td>

                {/* Actions */}
                {isAdmin && (
                  <td>
                    {renderMemberActions(m)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* === Pit Crew Recruitment Panel === */}
      {isAdmin && activePanel === "recruit" && (
        <div className="card" style={{
          padding: 0,
          overflow: "hidden",
          border: "1px solid var(--live-soft)",
        }}>
          {/* Section header */}
          <div style={{
            padding: "0.85rem 1.25rem",
            borderBottom: "1px solid var(--live-soft)",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            background: "transparent",
          }}>
            <div style={{
              width: "4px",
              height: "18px",
              background: "var(--live)",
              borderRadius: "2px",
              boxShadow: "none",
            }} />
            <h3 style={{
              fontFamily: "var(--font-display)",
              fontSize: "1.05rem",
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--text-strong)",
            }}>
              Recruit drivers
            </h3>
          </div>

          <div style={{ padding: "1.25rem" }}>
            {/* Invite by iRacing Customer ID */}
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{
                fontFamily: "var(--font-body)",
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "var(--text-secondary)",
                marginBottom: "0.5rem",
              }}>
                Invite by iRacing customer ID
              </div>

              <div style={{ display: "flex", gap: "0.5rem", alignItems: "stretch" }}>
                <input
                  className="input input-data"
                  placeholder="e.g. 123456"
                  value={inviteId}
                  onChange={(e) => setInviteId(e.target.value)}
                  style={{
                    maxWidth: 220,
                    background: "var(--ink-2)",
                    border: "1px solid var(--live-edge)",
                    fontSize: "0.85rem",
                  }}
                />
                <button
                  className="btn btn-secondary"
                  onClick={handleInvite}
                  style={{
                    borderColor: "var(--rule-strong)",
                  }}
                >
                  Invite
                </button>
              </div>
            </div>

            {/* Divider */}
            <div style={{
              height: "1px",
              background: "linear-gradient(90deg, transparent 0%, var(--live-soft) 20%, var(--live-soft) 80%, transparent 100%)",
              marginBottom: "1.5rem",
            }} />

            {/* Generate shareable link */}
            <div>
              <div style={{
                fontFamily: "var(--font-body)",
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "var(--text-secondary)",
                marginBottom: "0.5rem",
              }}>
                Shareable invite link
              </div>

              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleGenerateLink}
                  style={{
                    borderColor: "var(--rule-strong)",
                  }}
                >
                  Generate link
                </button>

                {inviteLink && (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    flex: 1,
                    minWidth: 0,
                  }}>
                    {/* Link display */}
                    <div style={{
                      flex: 1,
                      minWidth: 0,
                      padding: "0.45rem 0.75rem",
                      background: "var(--ink-2)",
                      border: "1px solid var(--live-soft)",
                      borderRadius: "var(--r-md)",
                      fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                      fontSize: "0.8125rem",
                      color: "var(--live)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                      {inviteLink}
                    </div>

                    {/* Copy button with feedback */}
                    <button
                      className="btn"
                      onClick={handleCopyLink}
                      style={{
                        padding: "0.45rem 0.85rem",
                        background: copied ? "var(--live-soft)" : "transparent",
                        color: copied ? "var(--live-strong)" : "var(--text-default)",
                        border: `1px solid ${copied ? "var(--live-edge)" : "var(--rule-strong)"}`,
                        transition: "all 0.2s cubic-bezier(0.22, 1, 0.36, 1)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {removeTarget && (
        <ConfirmModal
          open
          title="Remove driver?"
          body={
            <>
              <strong style={{ color: "var(--text-strong)" }}>{removeTarget.displayName || "This driver"}</strong>{" "}
              will be removed from {team?.name || "the team"} and will lose access to its runs and setups.
            </>
          }
          confirmLabel="Remove driver"
          tone="danger"
          loading={memberActionKey === `${removeTarget.userId}:remove`}
          onConfirm={() => void handleRemoveMember(removeTarget)}
          onCancel={() => setRemoveTarget(null)}
        />
      )}
    </div>
  );
}
